/**
 * Single source of truth for the WhoBrew route registry.
 *
 * Consumed by:
 *   - The React Router config (src/App.tsx)
 *   - The sitemap.xml generator (scripts/generate-sitemap.ts)
 *   - The breadcrumb component
 *   - The Footer link tree
 *
 * To add a route: add it here. The router, sitemap, and footer pick it up
 * automatically. Per the design thesis (D-009), routes never live in more
 * than one place.
 */

export type RouteKind =
  | "marketing"   // public, indexable, in PublicLayout
  | "legal"       // public, indexable, in PublicLayout
  | "ugc"         // user-generated, special render
  | "app"         // authenticated, noindex, in AppLayout
  | "guest"      // venue guest experience, noindex
  | "internal";  // /preview/* and similar — noindex, hidden from footer

export interface RouteDef {
  path: string;
  kind: RouteKind;
  /** Footer column. `null` = excluded from Footer. */
  footerColumn: "Product" | "For" | "Compare" | "Company" | "Legal" | null;
  /** Label as it appears in the Footer. */
  label?: string;
  /** Sitemap priority 0.0–1.0. */
  priority?: number;
  /** Sitemap changefreq. */
  changefreq?:
    | "always"
    | "hourly"
    | "daily"
    | "weekly"
    | "monthly"
    | "yearly"
    | "never";
}

export const ROUTES: RouteDef[] = [
  // Marketing
  { path: "/",                            kind: "marketing", footerColumn: null,      label: "Home",            priority: 1.0, changefreq: "weekly" },
  { path: "/how-it-works",                kind: "marketing", footerColumn: "Product", label: "How it works",    priority: 0.9, changefreq: "monthly" },
  { path: "/pricing",                     kind: "marketing", footerColumn: "Product", label: "Pricing",         priority: 0.9, changefreq: "monthly" },
  { path: "/teams",                       kind: "marketing", footerColumn: "For",     label: "Teams",           priority: 0.7, changefreq: "monthly" },
  { path: "/help",                        kind: "marketing", footerColumn: "Company", label: "Help",            priority: 0.6, changefreq: "monthly" },
  { path: "/contact",                     kind: "marketing", footerColumn: "Company", label: "Contact",         priority: 0.6, changefreq: "yearly" },
  { path: "/trust",                       kind: "marketing", footerColumn: "Company", label: "Trust",           priority: 0.6, changefreq: "monthly" },

  // Personas — /for/*
  { path: "/for/recruiters",              kind: "marketing", footerColumn: "For", label: "Recruiters",          priority: 0.8, changefreq: "monthly" },
  { path: "/for/sales",                   kind: "marketing", footerColumn: "For", label: "Sales",                priority: 0.8, changefreq: "monthly" },
  { path: "/for/real-estate",             kind: "marketing", footerColumn: "For", label: "Real estate",         priority: 0.8, changefreq: "monthly" },
  { path: "/for/consultants",             kind: "marketing", footerColumn: "For", label: "Consultants",          priority: 0.7, changefreq: "monthly" },
  { path: "/for/founders",                kind: "marketing", footerColumn: "For", label: "Founders",             priority: 0.7, changefreq: "monthly" },

  // Compare — /compare/whobrew-vs-*
  { path: "/compare/whobrew-vs-popl",     kind: "marketing", footerColumn: "Compare", label: "vs Popl",        priority: 0.7, changefreq: "monthly" },
  { path: "/compare/whobrew-vs-blinq",    kind: "marketing", footerColumn: "Compare", label: "vs Blinq",       priority: 0.7, changefreq: "monthly" },
  { path: "/compare/whobrew-vs-linq",     kind: "marketing", footerColumn: "Compare", label: "vs Linq",         priority: 0.6, changefreq: "monthly" },
  { path: "/compare/whobrew-vs-v1ce",     kind: "marketing", footerColumn: "Compare", label: "vs V1CE",         priority: 0.6, changefreq: "monthly" },

  // Legal
  { path: "/terms",                       kind: "legal",     footerColumn: "Legal",   label: "Terms",           priority: 0.3, changefreq: "yearly" },
  { path: "/privacy",                     kind: "legal",     footerColumn: "Legal",   label: "Privacy",         priority: 0.3, changefreq: "yearly" },

  // UGC
  { path: "/card/:username",              kind: "ugc",       footerColumn: null,      priority: 0.5, changefreq: "weekly" },
  { path: "/card/:username/form/:linkId", kind: "ugc",       footerColumn: null },

  // Specific-card view by id (D-001) — curated homepage demo only. noindex,
  // excluded from sitemap/footer; not a general per-card product surface.
  { path: "/card/id/:cardId",             kind: "internal",  footerColumn: null },

  // App (authenticated; noindex via robots + per-page meta)
  { path: "/my-card",                     kind: "app",       footerColumn: null },
  { path: "/insights",                    kind: "app",       footerColumn: null },
  { path: "/leads",                       kind: "app",       footerColumn: null },
  { path: "/email-signature",             kind: "app",       footerColumn: null },
  { path: "/profile",                     kind: "app",       footerColumn: null },
  { path: "/order-card",                  kind: "app",       footerColumn: null },
  { path: "/order-confirmation",          kind: "app",       footerColumn: null },
  { path: "/admin",                       kind: "app",       footerColumn: null },
  { path: "/venue/setup",                 kind: "app",       footerColumn: null },
  { path: "/venue/dashboard",             kind: "app",       footerColumn: null },

  // Guest
  { path: "/v/:slug",                     kind: "guest",     footerColumn: null },

  // Internal — kept reachable for design reference, but noindexed and not in footer
  { path: "/valent",                      kind: "internal",  footerColumn: null },
  { path: "/preview",                     kind: "internal",  footerColumn: null },
  { path: "/preview/a",                   kind: "internal",  footerColumn: null },
  { path: "/preview/b",                   kind: "internal",  footerColumn: null },
  { path: "/preview/c",                   kind: "internal",  footerColumn: null },
  { path: "/preview/d",                   kind: "internal",  footerColumn: null },
];

/** All routes that should appear in sitemap.xml. */
export const sitemapRoutes = ROUTES.filter(
  (r) => r.kind === "marketing" || r.kind === "legal"
);

/** All routes that should be reachable from the Footer, grouped by column. */
export function footerColumns() {
  const cols: Record<string, RouteDef[]> = {};
  for (const r of ROUTES) {
    if (r.footerColumn) {
      cols[r.footerColumn] = cols[r.footerColumn] || [];
      cols[r.footerColumn].push(r);
    }
  }
  return cols;
}

/** Origin used for canonical URLs and sitemap entries. */
export const SITE_ORIGIN = "https://whobrew.us";

export function canonicalFor(path: string): string {
  return `${SITE_ORIGIN}${path}`;
}
